import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import { WalletProvider } from "@advisa/wallet";
import { Notifications } from "@mantine/notifications";
import classes from "./App.module.css";
import { StatsigProvider } from "@statsig/react-bindings";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LanguageProvider } from "./components/languageToggle";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { usePageViewTracking } from "./hooks/api/usePageviewTracking";
import * as Sentry from "@sentry/react";
import { version } from "../package.json";
import { useUpdateCustomerData } from "./hooks/ui";
import ErrorComponent from "./components/errorComponent/";
import dayjs from "dayjs";
import "dayjs/locale/sv";
import localizedFormat from "dayjs/plugin/localizedFormat";
import {
  getDeviceType,
  getOperatingSystem,
  canOpenBankIDApp,
  isMobileDevice,
} from "./utils/useDeviceDetection";
import PageNotFound from "./components/pageNotFound";
import { statsigClient } from "./utils/statsig";

dayjs.extend(localizedFormat);
dayjs.locale("sv");

const isLocalhost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({
  routeTree,
  context: {
    queryClient,
    credentials: undefined!,
    browserInfo: undefined!,
  },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  defaultErrorComponent: () => {
    return <ErrorComponent />;
  },
  defaultNotFoundComponent: () => {
    return <PageNotFound />;
  },
});

const enableSentry =
  !isLocalhost &&
  (import.meta.env.MODE === "production" ||
    import.meta.env.MODE === "development");

if (enableSentry) {
  Sentry.init({
    dsn: "https://1e05b103818022051bdd3e99f498b345@o451393.ingest.us.sentry.io/4508726098264065",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.tanstackRouterBrowserTracingIntegration(router),
    ],
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.05 : 0.5,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,
    environment: import.meta.env.MODE,
    release: version,
    ignoreErrors: [],
    denyUrls: [],
  });
}

function getBrowserInfo() {
  const deviceType = getDeviceType();
  const os = getOperatingSystem();
  const canUseBankID = canOpenBankIDApp();
  const isMobile = isMobileDevice();

  return {
    userAgent: navigator.userAgent,
    isMobile: isMobile,
    deviceType: deviceType,
    os: os,
    isIOS: os === "iOS",
    isAndroid: os === "Android",
    canUseBankIDApp: canUseBankID,
    isTouch: navigator.maxTouchPoints > 0 || "ontouchstart" in window,
    touchPoints: navigator.maxTouchPoints,
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    language: navigator.language,
  };
}

function RouterWithContext() {
  const { customerData } = useUpdateCustomerData();
  const browserInfo = getBrowserInfo();

  return (
    <RouterProvider
      router={router}
      context={{
        credentials: customerData.credentials,
        browserInfo,
      }}
    />
  );
}

const App = () => {
  usePageViewTracking();
  const app = (
    <WalletProvider brand="Sambla">
      <Notifications
        position="bottom-center"
        classNames={{ root: classes.notifications }}
      />
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <RouterWithContext />
        </LanguageProvider>
      </QueryClientProvider>
    </WalletProvider>
  );

  return statsigClient ? (
    <StatsigProvider client={statsigClient}>{app}</StatsigProvider>
  ) : (
    app
  );
};

export default App;
